import {
  Button,
  Card,
  CardContent,
  Col,
  Flex,
  Form,
  Icon,
  Icons,
  Input,
  Row,
  Select,
  type FormInstance,
  type SelectOption,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { useCallback, useEffect, useMemo } from "react";
import { createDcCodeRules, createDcNameRules } from "./dcCodeValidation";

export type DistributionCenterFormMode = "create" | "edit";

export interface DistributionCenterFormValues {
  purchaserId: number | null;
  dcName: string;
  dcCode: string;
}

export interface PurchaserSelectOption {
  purchaserId: number;
  purchaserName: string;
}

interface MasterDataDistributionCenterAddEditFormProps {
  form: FormInstance<DistributionCenterFormValues>;
  mode: DistributionCenterFormMode;
  initialValues: DistributionCenterFormValues;
  isPurchaserLocked: boolean;
  lockedPurchaserName: string;
  purchaserOptions: PurchaserSelectOption[];
  purchasersLoading?: boolean;
  isSaving: boolean;
  isFormSubmittable: boolean;
  onSubmit: (values: DistributionCenterFormValues) => Promise<void> | void;
  onCancel: () => void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

function parsePurchaserIdFromSelect(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "object" && "value" in (value as object)) {
    const raw = (value as SelectOption).value;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

interface PurchaserSelectFieldProps {
  value?: number | null;
  onChange?: (value: number | null) => void;
  options: SelectOption[];
  isLoading?: boolean;
  selectKey: string;
}

const PurchaserSelectField = ({
  value,
  onChange,
  options,
  isLoading = false,
  selectKey,
}: PurchaserSelectFieldProps) => {
  const selected = options.find((option) => option.value === String(value));
  return (
    <Select
      key={`${selectKey}-${value ?? "none"}-${options.length}`}
      size="medium"
      isSearchable
      isClearable={false}
      isLoading={isLoading}
      placeholder="Select purchaser"
      options={options}
      defaultValue={selected}
      onChange={(next) => onChange?.(parsePurchaserIdFromSelect(next))}
      aria-label="Purchaser"
    />
  );
};

export const MasterDataDistributionCenterAddEditForm = ({
  form,
  mode,
  initialValues,
  isPurchaserLocked,
  lockedPurchaserName,
  purchaserOptions,
  purchasersLoading = false,
  isSaving,
  isFormSubmittable,
  onSubmit,
  onCancel,
  onFormValidityChange,
}: MasterDataDistributionCenterAddEditFormProps) => {
  const selectOptions = useMemo<SelectOption[]>(
    () =>
      purchaserOptions.map((purchaser) => ({
        value: String(purchaser.purchaserId),
        label: purchaser.purchaserName,
      })),
    [purchaserOptions],
  );

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const dcName = values.dcName?.trim() ?? "";
    const dcCode = values.dcCode?.trim() ?? "";
    const purchaserId = values.purchaserId;

    const hasRequiredValues =
      dcName !== "" &&
      dcCode !== "" &&
      (isPurchaserLocked || (purchaserId != null && Number(purchaserId) > 0));

    const isDirty =
      mode === "create"
        ? true
        : dcName !== (initialValues.dcName?.trim() ?? "") ||
          dcCode !== (initialValues.dcCode?.trim() ?? "");

    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [
    form,
    initialValues,
    isPurchaserLocked,
    mode,
    onFormValidityChange,
  ]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, updateFormValidity]);

  const dcNameRules = useMemo(() => createDcNameRules(), []);
  const dcCodeRules = useMemo(() => createDcCodeRules(), []);
  const purchaserRules = useMemo(
    () =>
      isPurchaserLocked
        ? []
        : [
            {
              validator: async (_: unknown, value: unknown) => {
                if (value == null || Number(value) <= 0) {
                  throw new Error("Purchaser is required");
                }
              },
            },
          ],
    [isPurchaserLocked],
  );

  return (
    <ErrorBoundary>
      <Card className="bg-white shadow-sm border-1 border-color-grey-25 rounded-medium mb-2">
        <CardContent className="p-3">
          <Form<DistributionCenterFormValues>
            form={form}
            onFinish={onSubmit}
            onFieldsChange={updateFormValidity}
            className="master-data-dc-add-edit"
          >
            <Flex align="center" justifyContent="space-between" gap={1}>
              <Row style={{ flex: 1 }}>
                <Col span={4}>
                  {isPurchaserLocked ? (
                    <Form.Item>
                      <Input
                        value={lockedPurchaserName}
                        isDisabled
                        aria-label="Purchaser"
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="purchaserId"
                      validateTrigger={["onChange", "onBlur"]}
                      rules={purchaserRules}
                    >
                      <PurchaserSelectField
                        options={selectOptions}
                        isLoading={purchasersLoading}
                        selectKey={`purchaser-select-${mode}-${initialValues.purchaserId ?? "new"}`}
                      />
                    </Form.Item>
                  )}
                </Col>
                <Col span={4}>
                  <Form.Item
                    name="dcName"
                    validateTrigger={["onChange", "onBlur"]}
                    rules={dcNameRules}
                  >
                    <Input placeholder="Enter DC Name" />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    name="dcCode"
                    validateTrigger={["onChange", "onBlur"]}
                    rules={dcCodeRules}
                  >
                    <Input placeholder="Enter DC Code" />
                  </Form.Item>
                </Col>
              </Row>
              <Flex align="center" gap={0.5}>
                <Button
                  size="small"
                  color="primary"
                  variant="clear"
                  onPress={() => form.submit()}
                  isLoading={isSaving}
                  isDisabled={isSaving || !isFormSubmittable}
                  aria-label="Save distribution center"
                >
                  <Icon name={Icons.Check} />
                </Button>
                <Button
                  size="small"
                  color="grey"
                  variant="clear"
                  onPress={onCancel}
                  isDisabled={isSaving}
                  aria-label="Cancel"
                >
                  <Icon name={Icons.Close} />
                </Button>
              </Flex>
            </Flex>
          </Form>
        </CardContent>
      </Card>
    </ErrorBoundary>
  );
};
